import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useSelector } from "react-redux";
import { isEqual } from "lodash";
import styles from "./style.module.scss";
import { RootState } from "../../../state";

ChartJS.register(
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

function OptimisationPbmsGraph() {
  const ressData = useSelector((state: RootState) => state.ress, isEqual);
  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );
  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const chartData = useMemo(
    () => ({
      datasets: Object.entries(ressData)
        .filter(([, carData]) => carData.ressPBMSkW.length > 0)
        .map(([carColour, carData]) => {
          const driverId = jagDrivers[carColour];
          const driver = driverId ? driverInfo[driverId] : undefined;
          const colour =
            driver?.driverHighlightColourAlt ||
            driver?.driverHighlightColour ||
            "#888";

          return {
            label: driver?.driverName || carColour,
            data: carData.ressPBMSkW.map((pbms, index) => ({
              x: index * 0.1,
              y: pbms,
            })),
            borderColor: colour,
            backgroundColor: colour,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 3,
            showLine: true,
            tension: 0,
            fill: false,
          };
        }),
    }),
    [ressData, jagDrivers, driverInfo],
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#ffffff",
          usePointStyle: true,
          pointStyle: "line" as const,
        },
      },
      title: {
        display: true,
        text: "Race Page PBMS",
        color: "#ffffff",
        font: {
          size: 14,
          weight: "bold" as const,
        },
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        title: {
          display: true,
          text: "Time (s)",
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
          maxTicksLimit: 6,
        },
        grid: {
          color: "#505050",
        },
      },
      y: {
        min: -800,
        max: 700,
        title: {
          display: true,
          text: "PBMS (kW)",
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
          maxTicksLimit: 5,
          callback: (value: string | number) =>
            value === 500 || value === -500 ? "" : value,
        },
        grid: {
          color: "#505050",
        },
      },
    },
  };

  return (
    <div className={styles.container}>
      <Line
        data={chartData}
        options={chartOptions}
        role="img"
        aria-label="Race Page PBMS against time"
      />
    </div>
  );
}

export default OptimisationPbmsGraph;
